import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import type { Tables } from '@/integrations/supabase/types';

export type Message = Tables<'messages'>;

export type MessageThread = {
  partnerId: string;
  partnerUsername: string;
  partnerAvatar: string | null;
  lastMessage: string;
  lastAt: string;
  unread: number;
};

/** All conversations grouped by partner */
export function useMessageThreads() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['message-threads', user?.id],
    enabled: !!user,
    queryFn: async () => {
      if (!user) return [];
      const { data: msgs, error } = await supabase
        .from('messages')
        .select('*')
        .or(`sender_id.eq.${user.id},recipient_id.eq.${user.id}`)
        .order('created_at', { ascending: false })
        .limit(500);
      if (error) throw error;
      if (!msgs?.length) return [];

      const partnerMap = new Map<string, MessageThread>();
      for (const m of msgs) {
        const partnerId = m.sender_id === user.id ? m.recipient_id : m.sender_id;
        const existing = partnerMap.get(partnerId);
        const isUnread = m.recipient_id === user.id && !m.read_at;
        if (!existing) {
          partnerMap.set(partnerId, {
            partnerId,
            partnerUsername: '',
            partnerAvatar: null,
            lastMessage: m.body,
            lastAt: m.created_at,
            unread: isUnread ? 1 : 0,
          });
        } else if (isUnread) {
          existing.unread += 1;
        }
      }

      const partnerIds = [...partnerMap.keys()];
      if (partnerIds.length) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('user_id, username, avatar_emoji')
          .in('user_id', partnerIds);
        (profiles || []).forEach(p => {
          const t = partnerMap.get(p.user_id);
          if (t) {
            t.partnerUsername = p.username;
            t.partnerAvatar = p.avatar_emoji;
          }
        });
      }

      return [...partnerMap.values()].sort(
        (a, b) => new Date(b.lastAt).getTime() - new Date(a.lastAt).getTime(),
      );
    },
  });
}

/** Messages with one specific partner */
export function useThreadMessages(partnerId: string | null) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['thread', user?.id, partnerId],
    enabled: !!user && !!partnerId,
    queryFn: async () => {
      if (!user || !partnerId) return [];
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .or(
          `and(sender_id.eq.${user.id},recipient_id.eq.${partnerId}),and(sender_id.eq.${partnerId},recipient_id.eq.${user.id})`,
        )
        .order('created_at', { ascending: true })
        .limit(200);
      if (error) throw error;
      return (data || []) as Message[];
    },
  });
}

export function useSendMessage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      recipientId,
      body,
      tradeListingId,
    }: {
      recipientId: string;
      body: string;
      tradeListingId?: string;
    }) => {
      if (!user) throw new Error('Not authenticated');
      const { error } = await supabase.from('messages').insert({
        sender_id: user.id,
        recipient_id: recipientId,
        body,
        trade_listing_id: tradeListingId ?? null,
      });
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['thread', user?.id, vars.recipientId] });
      qc.invalidateQueries({ queryKey: ['message-threads', user?.id] });
    },
  });
}

export function useMarkThreadRead(partnerId: string | null) {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      if (!user || !partnerId) return;
      await supabase
        .from('messages')
        .update({ read_at: new Date().toISOString() })
        .eq('sender_id', partnerId)
        .eq('recipient_id', user.id)
        .is('read_at', null);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['message-threads', user?.id] });
    },
  });
}

/** Realtime: refresh threads & open thread on new messages */
export function useMessagesRealtime() {
  const { user } = useAuth();
  const qc = useQueryClient();
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel('messages-rt')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'messages' },
        () => {
          qc.invalidateQueries({ queryKey: ['message-threads', user.id] });
          qc.invalidateQueries({ queryKey: ['thread', user.id] });
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, qc]);
}

/** Total unread badge */
export function useUnreadCount() {
  const { data } = useMessageThreads();
  return (data || []).reduce((s, t) => s + t.unread, 0);
}
